import React, { useMemo } from 'react';
import classNames from 'classnames';
import styles from './ProgressBarBoxes.module.scss';
import nextId from 'react-id-generator';
import { ArrowRight } from '../../assets/svg/menu/ArrowRight';
import { ArrowLeft } from '../../assets/svg/menu/ArrowLeft';
import enArrowDirection from '../../../api/enums/enArrowDirection';
import CarrierDetailsPopup, {
    CarrierDetailsPopupData,
    CarrierDetailsPopupDataKeys,
} from '../carrier-details-popup/CarrierDetailsPopup';
import {
    StyledDetailsWrapper,
    StyledLeftSection,
    StyledSquare,
    StyledPayloadType,
    StyledOutOfStock,
} from './styled';
import { useTranslation } from 'react-i18next';
import { IdType, useDataSource } from 'fulogics-ui-core';
import { connect } from 'react-redux';
import { iRootDispatch, iRootState } from '../../../store/store';
import { DataSourceOptions } from 'devextreme/data/data_source';
import { CarrierTypesStore } from '../../../store/models/CarrierTypesModel';
import useMemoDataSource from 'fulogics-ui-web/dist/layout/hooks/useMemoDataSource';

const getProgressBarr = (
    totalCount: number = 0,
    count: number = 0,
    arrowDirection?: enArrowDirection,
    canBeLoaded?: boolean
) => {
    const table = [];
    const id = nextId();

    const diff = totalCount ? count / totalCount : 0;
    const progressStatus = !canBeLoaded || diff > 1 ? 'error' : diff > 0.8 ? 'warning' : 'correct';

    if (totalCount) {
        if (arrowDirection == enArrowDirection.Right)
            table.push(<ArrowRight key={totalCount + 2} />);
        else table.push(<ArrowLeft key={totalCount + 2} />);
        for (let i = 0; i < totalCount; i++) {
            if (count && i < count) {
                table.push(<StyledSquare key={`${id}-${i}`} type={progressStatus} />);
            } else {
                table.push(<div key={`${id}-${i}`} className={classNames(styles.emptySquare)} />);
            }
        }
        return table;
    } else {
        return null;
    }
};

const mapStateToProps = (
    state: iRootState,
    ownProps: ProgressBarBoxesDataProps
): ProgressBarBoxesDataProps => ({
    createCarrierTypeDataSource: () => {
        return {
            store: CarrierTypesStore,
            select: CarrierDetailsPopupDataKeys,
        } as DataSourceOptions;
    },
});

const mapDispatch = (_dispatch: iRootDispatch, _ownDispatch: {}) => ({});

const ProgressBarBoxes = connect(
    mapStateToProps,
    mapDispatch as any
)((props: ProgressBarBoxesDataProps) => {
    const { t } = useTranslation('ProgressBarBoxes');
    const carrierTypeDataSource = useMemoDataSource(
        () => props.createCarrierTypeDataSource?.(),
        [props.createCarrierTypeDataSource]
    );
    const { data, loading } = useDataSource<CarrierDetailsPopupData>(carrierTypeDataSource as any);

    const canBeLoaded = !props.remainingTrailerSpace || props?.remainingTrailerSpace?.canBeLoaded;

    const outOfStock =
        !canBeLoaded ||
        (props.maxItemsCount && props.itemsCount ? props.itemsCount / props.maxItemsCount : 0) > 1;

    const bar = useMemo(
        () =>
            getProgressBarr(
                props.maxItemsCount,
                props.itemsCount,
                props.arrowDirection,
                canBeLoaded
            ),
        [props.itemsCount, props.maxItemsCount, canBeLoaded]
    );

    const availableSpace = Math.ceil(
        props.remainingTrailerSpace?.remainingSpaceByCarrierTypes?.find((type) => type?.isDefault)
            ?.remainingECEquivalentSpace || 0
    );

    return (
        <div className={classNames(styles.container, props.className)}>
            <div className={classNames(styles.top)}>
                <div className={classNames(styles.name)}>{props.name}</div>
                <StyledLeftSection>
                    <label>{t('availableSpace')}</label>
                    {outOfStock ? (
                        <StyledOutOfStock>{t('outOfStock')}</StyledOutOfStock>
                    ) : (
                        <StyledPayloadType>
                            {t('payloadType', {
                                availableSpace,
                            })}
                        </StyledPayloadType>
                    )}
                    {!props.hidePayloadDetails && (
                        <CarrierDetailsPopup
                            remainingSpace={
                                props.remainingTrailerSpace?.remainingSpaceByCarrierTypes
                            }
                            data={data}
                            loading={loading}
                        >
                            <StyledDetailsWrapper>{t('details')}</StyledDetailsWrapper>
                        </CarrierDetailsPopup>
                    )}
                </StyledLeftSection>
            </div>
            <div className={classNames(styles.bottom)}>{bar}</div>
        </div>
    );
});

export default ProgressBarBoxes;

export type ProgressBarBoxesDataProps = ProgressBarBoxesDisplayOptions;

export class ProgressBarBoxesDisplayOptions {
    className?: string;
    name?: string;
    itemsCount?: number;
    maxItemsCount?: number;
    arrowDirection?: enArrowDirection;
    remainingTrailerSpace?: RemainingTrailerSpace;
    //
    hidePayloadDetails?: boolean;
    //
    createCarrierTypeDataSource?: () => DataSourceOptions;
}

export type RemainingTrailerSpace = {
    canBeLoaded?: boolean;
    currentlyFilledECEquivalentSpace?: number;
    remainingSpaceByCarrierTypes?: {
        isDefault?: boolean;
        carrierTypeId?: IdType;
        remainingSpace?: number;
        remainingECEquivalentSpace?: number;
    }[];
};
